import type { Section, SectionOverride } from './entities';

export const sectionMenus: Section[] = [
  {
    key: 'markets-news',
    label: 'Markets & News',
    shortLabel: 'Markets',
    href: '/markets',
    blurb: 'Breaking headlines, the trading day, and the calendars that move it.',
    color: 'markets',
    columns: [
      {
        heading: 'News',
        links: [
          { label: 'Breaking News', href: '/latest' },
          { label: 'Tech', href: '/topic/technology-stocks' },
          { label: 'Politics', href: '/politics' },
          { label: 'Health Care', href: '/general/health-care' },
        ],
      },
      {
        heading: 'Markets',
        links: [
          { label: 'Premarket', href: '/premarket' },
          { label: 'Market Update', href: '/markets' },
          { label: 'After Hours', href: '/after-hours-center' },
          { label: 'Top Movers', href: '/movers' },
        ],
      },
      {
        heading: 'Calendars',
        links: [
          { label: 'Earnings', href: '/earnings' },
          { label: 'Economic', href: '/calendars/economic' },
          { label: 'IPO', href: '/calendars/ipos' },
          { label: 'Dividend', href: '/dividends' },
        ],
      },
    ],
    related: {
      heading: 'Related across Benzinga',
      links: [
        { label: 'Top Movers', href: '/movers', accent: 'stocks' },
        { label: "Today's Rating Changes", href: '/analyst-stock-ratings', accent: 'ratings' },
      ],
    },
  },
  {
    key: 'stocks-sectors',
    label: 'Stocks & Sectors',
    shortLabel: 'Stocks',
    href: '/quote',
    blurb: 'Research any ticker, then drill into sectors, ratings, and options.',
    color: 'stocks',
    columns: [
      {
        heading: 'Research a Stock',
        links: [
          { label: 'Look Up Any Quote', href: '/quote' },
          { label: 'Top Movers', href: '/movers' },
          { label: 'Premarket Movers', href: '/premarket' },
        ],
      },
      {
        heading: 'Sector News',
        links: [
          { label: 'Technology', href: '/topic/technology-stocks' },
          { label: 'Health Care', href: '/general/health-care' },
        ],
      },
      {
        heading: 'On a Quote Page',
        links: [
          { label: 'Analyst Ratings', href: '/analyst-stock-ratings' },
          { label: 'Insider Trades', href: '/sec/insider-trades/search' },
          { label: 'Options Data', href: '/markets/options' },
          { label: 'Earnings', href: '/earnings' },
        ],
      },
    ],
    related: {
      heading: 'Related across Benzinga',
      links: [
        { label: 'Stock Screener', href: '/screener', accent: 'screeners' },
        { label: 'Options Activity', href: '/calendars/unusual-options-activity', accent: 'trading' },
      ],
    },
  },
  {
    key: 'ratings-research',
    label: 'Ratings & Research',
    shortLabel: 'Ratings',
    href: '/analyst-stock-ratings',
    blurb: "Wall Street calls, insider moves, and Benzinga's own stock rankings.",
    color: 'ratings',
    columns: [
      {
        heading: 'Analyst Activity',
        links: [
          { label: 'Upgrades', href: '/analyst-stock-ratings/upgrades' },
          { label: 'Downgrades', href: '/analyst-stock-ratings/downgrades' },
          { label: 'Price-Target Changes', href: '/price-target' },
          { label: 'All Analyst Ratings', href: '/analyst-stock-ratings' },
        ],
      },
      {
        heading: 'Ownership & Flows',
        links: [
          { label: 'Insider Trades (SEC)', href: '/sec/insider-trades/search' },
          { label: 'Government / Congress Trades', href: '/government-trades' },
        ],
      },
      {
        heading: 'Benzinga Stock Rankings',
        links: [
          { label: 'Top Value Stocks', href: '/screener/top-value-stocks' },
          { label: 'Top Growth Stocks', href: '/screener/top-growth-stocks' },
          { label: 'Top Quality Stocks', href: '/screener/top-quality-stocks' },
          { label: 'All Ranking Screens', href: '/screener' },
        ],
      },
    ],
    related: {
      heading: 'Related across Benzinga',
      links: [
        { label: 'Top-Ranked Screen', href: '/screener/top-value-stocks', accent: 'screeners' },
        { label: 'Ratings Feed', href: '/analyst-stock-ratings/upgrades', accent: 'markets' },
      ],
    },
  },
  {
    key: 'trading',
    label: 'Trading',
    shortLabel: 'Trading',
    href: '/markets/options',
    blurb: 'Options flow, trade calculators, and ideas for active traders.',
    color: 'trading',
    columns: [
      {
        heading: 'Options',
        links: [
          { label: 'Options Hub', href: '/markets/options' },
          { label: 'Options Activity', href: '/calendars/unusual-options-activity' },
        ],
      },
      {
        heading: 'Tools',
        links: [
          { label: 'Options Profit Calculator', href: '/tools/options-profit-calculator' },
          { label: 'Covered Call Calculator', href: '/tools/covered-call-calculator' },
        ],
      },
      {
        heading: 'Learn to Trade',
        links: [{ label: 'Pro Blog', href: '/pro/blog', note: 'Trading education & ideas' }],
      },
    ],
    related: {
      heading: 'Related across Benzinga',
      links: [
        { label: 'Options Activity', href: '/calendars/unusual-options-activity', accent: 'stocks' },
        { label: 'Try Benzinga Pro', href: '/pro/', accent: 'brand' },
      ],
    },
  },
  {
    key: 'screeners-lists',
    label: 'Screeners & Lists',
    shortLabel: 'Screeners',
    href: '/screener',
    blurb: 'Filter the market your way, or start from a curated list.',
    color: 'screeners',
    columns: [
      {
        heading: 'Screeners',
        links: [
          { label: 'Stock & ETF Screener', href: '/screener' },
        ],
      },
      {
        heading: 'Popular Lists',
        links: [
          { label: 'Best Dividend Stocks', href: '/money/best-dividend-stocks-to-buy-and-hold' },
          { label: 'Best Growth Stocks', href: '/money/best-growth-stocks' },
          { label: 'Best AI Stocks', href: '/money/best-artificial-intelligence-stocks' },
          { label: 'Top Stocks Under $5', href: '/money/top-stocks-under-5' },
        ],
      },
      {
        heading: 'Rankings by Strategy',
        links: [
          { label: 'Value', href: '/screener/top-value-stocks' },
          { label: 'Growth', href: '/screener/top-growth-stocks' },
          { label: 'Momentum', href: '/screener/top-momentum-stocks' },
          { label: 'Quality', href: '/screener/top-quality-stocks' },
        ],
      },
    ],
    related: {
      heading: 'Related across Benzinga',
      links: [
        { label: 'Live Market News', href: '/latest', accent: 'markets' },
        { label: 'How to Invest', href: '/money/how-to-invest-money', accent: 'money' },
      ],
    },
  },
  {
    key: 'personal-finance',
    label: 'Learn',
    shortLabel: 'Learn',
    ctaLabel: 'Start Learning',
    href: '/learn',
    blurb: "Pick a broker, grow your savings, and protect what you've built.",
    color: 'money',
    columns: [
      {
        heading: 'Invest',
        links: [
          { label: 'Crypto', href: '/learn/cryptocurrency' },
          { label: 'Equities', href: '/learn/equities' },
          { label: 'Prediction Markets', href: '/learn/prediction-markets' },
        ],
      },
      {
        heading: 'Manage',
        links: [{ label: 'Debt Management', href: '/learn/debt-management' }],
      },
    ],
    related: {
      heading: 'Related across Benzinga',
      links: [
        { label: 'Top Value Stocks', href: '/screener/top-value-stocks', accent: 'screeners' },
        { label: 'Live Market News', href: '/latest', accent: 'markets' },
      ],
    },
  },
];

export const getSectionMenus = (override?: SectionOverride): Section[] => {
  if (!override?.length) {
    return sectionMenus;
  }
  const overridesByKey = new Map(override.map(entry => [entry.key, entry]));
  return sectionMenus.map(section => {
    const entry = overridesByKey.get(section.key);
    return entry ? { ...section, ...entry, key: section.key } : section;
  });
};
